/**
 * /admin/billing/plans — Plan Management
 *
 * Superadmin page to manage subscription package names, descriptions,
 * feature bullet points, and monthly/annual prices.
 *
 * Calls velaro-messaging superadmin/billing/packages and superadmin/billing/prices endpoints.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";

interface PriceRow {
  id: number;
  interval: string;
  baseMonthlyPrice: number;
  billedAmount: number;
  discountPercent: number;
  isActive: boolean;
}

interface PackageRow {
  id: number;
  name: string;
  description: string;
  color: string;
  featuresJson: string | null;
  isSystemPackage: boolean;
  isActive: boolean;
  sortOrder: number;
  prices: PriceRow[];
}

const COLOR_OPTIONS = [
  { value: "blue", label: "Blue", cls: "bg-cornflower-blue-500" },
  { value: "purple", label: "Purple", cls: "bg-purple-500" },
  { value: "emerald", label: "Emerald", cls: "bg-emerald-500" },
  { value: "orange", label: "Orange", cls: "bg-orange-500" },
  { value: "rose", label: "Rose", cls: "bg-rose-500" },
  { value: "slate", label: "Slate", cls: "bg-slate-500" },
];

const INTERVALS = ["monthly", "annual", "quarterly", "biannual", "triennial"];

function featuresToText(json: string | null): string {
  if (!json) return "";
  try { return (JSON.parse(json) as string[]).join("\n"); } catch { return json; }
}

function textToFeaturesJson(text: string): string {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  return JSON.stringify(lines);
}

export default function BillingPlansPage() {
  const api = useApi();
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Edit state
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editColor, setEditColor] = useState("blue");
  const [editFeatures, setEditFeatures] = useState("");
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [editActive, setEditActive] = useState(true);

  // Price edit state: { [priceId | "new-monthly" | "new-annual"]: { baseMonthlyPrice, billedAmount, discountPercent } }
  const [priceEdits, setPriceEdits] = useState<Record<string, { baseMonthlyPrice: string; billedAmount: string; discountPercent: string; interval: string }>>({});
  const [addPriceInterval, setAddPriceInterval] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await (await api.messaging.get("superadmin/billing/packages")).json();
      setPackages(Array.isArray(data) ? data : []);
    } catch {
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  function startEdit(pkg: PackageRow) {
    setEditId(pkg.id);
    setEditName(pkg.name);
    setEditDesc(pkg.description ?? "");
    setEditColor(pkg.color ?? "blue");
    setEditFeatures(featuresToText(pkg.featuresJson));
    setEditSortOrder(pkg.sortOrder);
    setEditActive(pkg.isActive);
    // Pre-populate price edits
    const pe: typeof priceEdits = {};
    pkg.prices.forEach(p => {
      pe[String(p.id)] = {
        baseMonthlyPrice: String(p.baseMonthlyPrice),
        billedAmount: String(p.billedAmount),
        discountPercent: String(p.discountPercent),
        interval: p.interval,
      };
    });
    setPriceEdits(pe);
    setAddPriceInterval("");
    setMsg("");
  }

  function cancelEdit() {
    setEditId(null);
    setMsg("");
  }

  async function savePackage() {
    if (!editId) return;
    setSaving(true);
    setMsg("");
    try {
      const resp = await api.messaging.put(`superadmin/billing/packages/${editId}`, {
        name: editName,
        description: editDesc,
        color: editColor,
        featuresJson: textToFeaturesJson(editFeatures),
        sortOrder: editSortOrder,
        isActive: editActive,
      });
      const result = await resp.json().catch(() => null);
      if (!result?.success) {
        setMsg(result?.error ?? "Failed to save package.");
        return;
      }

      // Save any price edits
      for (const [key, pe] of Object.entries(priceEdits)) {
        const priceId = isNaN(Number(key)) ? 0 : Number(key);
        const pkg = packages.find(p => p.id === editId);
        const existingPrice = pkg?.prices.find(p => p.id === priceId);

        await api.messaging.post("superadmin/billing/prices", {
          id: priceId > 0 ? priceId : 0,
          packageId: editId,
          interval: pe.interval,
          baseMonthlyPrice: parseFloat(pe.baseMonthlyPrice) || 0,
          billedAmount: parseFloat(pe.billedAmount) || 0,
          discountPercent: parseInt(pe.discountPercent) || 0,
          isActive: existingPrice?.isActive ?? true,
          currency: "usd",
        });
      }

      setMsg("Saved successfully.");
      setEditId(null);
      await load();
    } catch {
      setMsg("Network error saving changes.");
    } finally {
      setSaving(false);
    }
  }

  function addNewPrice() {
    if (!addPriceInterval) return;
    const key = `new-${addPriceInterval}-${Date.now()}`;
    setPriceEdits(prev => ({
      ...prev,
      [key]: { baseMonthlyPrice: "0", billedAmount: "0", discountPercent: "0", interval: addPriceInterval },
    }));
    setAddPriceInterval("");
  }

  if (loading) {
    return <div className="p-8 text-slate-500">Loading plans...</div>;
  }

  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Plan Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Edit plan names, descriptions, feature bullets, and pricing. Changes appear on the customer checkout page immediately.
          </p>
        </div>
        <button
          onClick={() => setHelpOpen(o => !o)}
          className="shrink-0 ml-4 flex items-center gap-1.5 text-xs text-cornflower-blue-600 hover:text-cornflower-blue-800 border border-cornflower-blue-200 rounded-lg px-3 py-1.5 bg-cornflower-blue-50 hover:bg-cornflower-blue-100 transition"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helpOpen ? "Hide Help" : "How this works"}
        </button>
      </div>

      {helpOpen && (
        <div className="mb-6 bg-cornflower-blue-50 border border-cornflower-blue-100 rounded-xl p-5 text-sm text-slate-700 space-y-4">
          <div>
            <p className="font-semibold text-slate-800 mb-1">What this page does</p>
            <p className="text-slate-600">
              This page controls everything customers see on the <strong>checkout page</strong> when they choose a plan.
              Changes here take effect immediately — no code deploy needed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Plan Name &amp; Description</p>
              <p className="text-slate-600 text-xs">
                The plan name (e.g. "Starter") and description appear at the top of each checkout card.
                Changing the name here also changes what appears on invoices.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Feature Bullets</p>
              <p className="text-slate-600 text-xs">
                One feature per line. These are the checkmark bullets shown on each plan card.
                Keep them short — 3–6 words each. The first bullet should be the strongest differentiator.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Display Price vs Billed Amount</p>
              <p className="text-slate-600 text-xs">
                <strong>Display $/mo</strong> — what the customer sees on the card ("$89/mo").
                <br />
                <strong>Billed Amount</strong> — what Stripe actually charges. For annual plans this is the full year (e.g. $1,068).
                For monthly it equals the display price.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Discount %</p>
              <p className="text-slate-600 text-xs">
                Shown as "Save X%" on the annual toggle. Set this to 10 for a 10% annual discount.
                Make sure the Billed Amount already reflects the discounted price — the % is display-only.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Sort Order</p>
              <p className="text-slate-600 text-xs">
                Lower number = shown first. Starter should be 10, Professional 20, Enterprise 30.
                Leave gaps (10, 20, 30) so you can insert plans between them later.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-2">Active / Inactive</p>
              <p className="text-slate-600 text-xs">
                Inactive plans are hidden from the checkout page. Use this to temporarily remove
                a plan without deleting it. Enterprise is kept inactive by default (contact sales only).
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
            <strong>Price changes</strong> only affect <em>new</em> checkout sessions. Existing subscriptions keep
            their agreed price until you manually update them in Subscription Settings.
          </div>
        </div>
      )}


      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
          msg.toLowerCase().includes("success") ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {msg}
        </div>
      )}

      <div className="space-y-4">
        {packages.map(pkg => (
          <div key={pkg.id} className={`bg-white rounded-xl border ${editId === pkg.id ? "border-cornflower-blue-400 shadow-md" : "border-slate-200 shadow-sm"}`}>
            {editId === pkg.id ? (
              /* ── Edit form ── */
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Plan Name</label>
                    <input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cornflower-blue-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Card Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {COLOR_OPTIONS.map(c => (
                        <button
                          key={c.value}
                          onClick={() => setEditColor(c.value)}
                          title={c.label}
                          className={`w-8 h-8 rounded-full ${c.cls} ring-2 ring-offset-1 transition ${editColor === c.value ? "ring-cornflower-blue-500" : "ring-transparent"}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
                    <input
                      value={editDesc}
                      onChange={e => setEditDesc(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cornflower-blue-400 focus:outline-none"
                      placeholder="Short description shown under the plan name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Feature Bullets <span className="font-normal text-slate-400">(one per line — shown on checkout cards)</span>
                    </label>
                    <textarea
                      value={editFeatures}
                      onChange={e => setEditFeatures(e.target.value)}
                      rows={6}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-cornflower-blue-400 focus:outline-none"
                      placeholder={"Live Chat\nEmail & SMS\nAI Chatbot\nBasic Analytics"}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Sort Order</label>
                    <input
                      type="number"
                      value={editSortOrder}
                      onChange={e => setEditSortOrder(Number(e.target.value))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cornflower-blue-400 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      id={`active-${pkg.id}`}
                      checked={editActive}
                      onChange={e => setEditActive(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor={`active-${pkg.id}`} className="text-sm text-slate-700">Active (visible on checkout)</label>
                  </div>
                </div>

                {/* Prices */}
                <div className="border-t border-slate-100 pt-4 mt-2">
                  <h3 className="text-xs font-semibold text-slate-600 mb-3">Pricing</h3>
                  <div className="space-y-3">
                    {Object.entries(priceEdits).map(([key, pe]) => (
                      <div key={key} className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-medium text-slate-500 w-20 shrink-0 capitalize">{pe.interval}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">Display $/mo</span>
                          <input
                            type="number"
                            value={pe.baseMonthlyPrice}
                            onChange={e => setPriceEdits(prev => ({ ...prev, [key]: { ...prev[key], baseMonthlyPrice: e.target.value } }))}
                            className="w-24 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">Billed amount</span>
                          <input
                            type="number"
                            value={pe.billedAmount}
                            onChange={e => setPriceEdits(prev => ({ ...prev, [key]: { ...prev[key], billedAmount: e.target.value } }))}
                            className="w-28 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">Discount %</span>
                          <input
                            type="number"
                            value={pe.discountPercent}
                            onChange={e => setPriceEdits(prev => ({ ...prev, [key]: { ...prev[key], discountPercent: e.target.value } }))}
                            className="w-16 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add new interval */}
                  <div className="flex items-center gap-2 mt-3">
                    <select
                      value={addPriceInterval}
                      onChange={e => setAddPriceInterval(e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
                    >
                      <option value="">+ Add interval...</option>
                      {INTERVALS.filter(i => !Object.values(priceEdits).some(p => p.interval === i)).map(i => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                    {addPriceInterval && (
                      <button
                        onClick={addNewPrice}
                        className="text-xs text-cornflower-blue-600 hover:underline"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
                  <button
                    onClick={savePackage}
                    disabled={saving}
                    className="bg-cornflower-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-cornflower-blue-700 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="text-slate-500 text-sm hover:text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* ── Read-only row ── */
              <div className="flex items-start justify-between p-4 gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-3 h-3 rounded-full mt-1.5 shrink-0 ${
                    pkg.color === "blue" ? "bg-cornflower-blue-500"
                    : pkg.color === "purple" ? "bg-purple-500"
                    : pkg.color === "emerald" ? "bg-emerald-500"
                    : pkg.color === "orange" ? "bg-orange-500"
                    : pkg.color === "rose" ? "bg-rose-500"
                    : "bg-slate-500"
                  }`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800 text-sm">{pkg.name}</span>
                      {!pkg.isActive && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Inactive</span>}
                      {pkg.isSystemPackage && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">System</span>}
                    </div>
                    {pkg.description && <p className="text-xs text-slate-500 mt-0.5">{pkg.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-2">
                      {pkg.prices.map(p => (
                        <span key={p.id} className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                          {p.interval}: ${p.baseMonthlyPrice}/mo {p.interval !== "monthly" ? `· billed $${p.billedAmount}` : ""}
                          {p.discountPercent > 0 ? ` (${p.discountPercent}% off)` : ""}
                          {!p.isActive ? " · inactive" : ""}
                        </span>
                      ))}
                      {pkg.prices.length === 0 && <span className="text-xs text-slate-400 italic">No prices set</span>}
                    </div>
                    {pkg.featuresJson && (() => {
                      try {
                        const feats = JSON.parse(pkg.featuresJson) as string[];
                        return (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {feats.slice(0, 4).map((f, i) => (
                              <span key={i} className="text-xs text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">{f}</span>
                            ))}
                            {feats.length > 4 && <span className="text-xs text-slate-400">+{feats.length - 4} more</span>}
                          </div>
                        );
                      } catch { return null; }
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => startEdit(pkg)}
                  className="shrink-0 text-sm text-cornflower-blue-600 hover:text-cornflower-blue-800 font-medium"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400 mt-6">
        Changes to plan names and features appear on the customer checkout page immediately. Price changes affect new checkout sessions — existing subscriptions are not modified.
      </p>
    </div>
  );
}
